/**
 * Centralised user-facing copy for the RBAC event-grant / COI / guest-block
 * surfaces (PRO-413, §5.2/§5.3). Server-authoritative messages (COI verdicts,
 * `rbac.guest_upgrade_required`) are surfaced from the API response where the
 * backend returns them; these constants are the FE-side fallbacks and static
 * labels so copy is defined in ONE place, not scattered across components.
 */

export const RBAC_STRINGS = {
	guest: {
		/** Mirrors backend `rbac.guest_upgrade_required`. */
		upgradeRequired:
			"This action requires a full company account. Upgrade to unlock it.",
		upgradeCta: "Upgrade account",
		viewOnly: "View only",
	},
	invite: {
		title: "Invite to evaluation panel",
		emailLabel: "Email address",
		emailPlaceholder: "name@company.com",
		roleLabel: "Panel role",
		rolePlaceholder: "Select a role",
		submit: "Send invite",
		cancel: "Cancel",
		success: "Invitation sent.",
		/** Fallback when the server does not return a specific COI message. */
		coiBlocked:
			"This person cannot be invited: they are linked to a bidding organisation on this RFx (conflict of interest).",
		genericError: "Could not send the invitation. Please try again.",
	},
	coi: {
		title: "Resolve conflict of interest",
		removeLabel: "Remove from panel",
		removeHelp:
			"Removes this member's panel access. Their entered scores are excluded from the evaluation.",
		overrideLabel: "Retain with documented override",
		overrideHelp:
			"Keeps this member on the panel. A documented reason is required and is recorded to the audit trail.",
		reasonLabel: "Override reason",
		reasonPlaceholder: "Document why this member is retained despite the conflict…",
		reasonRequired: "A documented reason is required to override.",
		confirmRemove: "Remove member",
		confirmOverride: "Retain with override",
		cancel: "Cancel",
		removeSuccess: "Member removed from the panel.",
		overrideSuccess: "Conflict overridden and recorded.",
		genericError: "Could not resolve the conflict. Please try again.",
		unauthorised: "You do not have permission to resolve this conflict.",
	},
	state: {
		pending: "Pending acceptance",
		accepted: "Active",
		released: "Released to read-only",
		readOnly: "Read only",
		suspended: "Suspended (COI)",
		flagged: "Conflict flagged",
		declined: "Declined",
		revoked: "Revoked",
	},
} as const;

export const PANEL_EVENT_ROLE_OPTIONS = [
	{ label: "Panel member", value: "panel_member" },
	{ label: "Moderator", value: "moderator" },
	{ label: "Observer", value: "observer" },
] as const;

export const APPROVER_EVENT_ROLE_OPTIONS = [
	{ label: "RFx approver", value: "rfx_approver" },
	{ label: "Bid approver", value: "bid_approver" },
	{ label: "Bid observer", value: "bid_observer" },
] as const;
